import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowRight, Quote, ArrowUpRight } from "lucide-react";
import { useI18n, pickLang } from "@/lib/I18nProvider";
import { TESTIMONIALS, PAGE } from "@/lib/pageContent";
import { PARTNERS } from "@/lib/i18n";
import { REFERENCE_CLIENTS } from "@/lib/extendedContent";

export default function Clients() {
  const { lang, t } = useI18n();
  const testimonials = pickLang(TESTIMONIALS, lang);
  const p = pickLang(PAGE, lang);

  return (
    <div data-testid="clients-page">
      <section className="relative overflow-hidden bg-radial-glow">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32">
          <div className="overline">{p.clients_overline}</div>
          <h1 className="font-display mt-6 text-4xl sm:text-5xl md:text-7xl font-bold tracking-tighter leading-[1.02] md:leading-[0.95] max-w-4xl text-white">
            {p.clients_title_a} <span className="gradient-text">{p.clients_title_b}</span>
          </h1>
          <p className="mt-8 text-base md:text-xl text-slate-200 max-w-2xl leading-relaxed">{p.clients_subtitle}</p>
          <p className="mt-4 text-xs font-mono uppercase tracking-widest text-slate-400">
            {lang === "fr" ? "* Marques de référence — anonymisées sous accord client" : lang === "es" ? "* Marcas de referencia — anonimizadas por acuerdo con el cliente" : lang === "pt" ? "* Marcas de referência — anonimizadas por acordo de cliente" : "* Reference brands — anonymized under client agreement"}
          </p>
        </div>
      </section>

      {/* Branded reference cards */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {REFERENCE_CLIENTS.map((c, i) => {
              const localizedDesc = c.description
                ? (typeof c.description === "string" ? c.description : (c.description[lang] || c.description.en))
                : (lang === "fr" ? `Streaming OTT multi-plateforme déployé sur ${c.region}. Tier ${c.category}.`
                  : lang === "es" ? `Streaming OTT multi-plataforma desplegado en ${c.region}. Tier ${c.category}.`
                  : lang === "pt" ? `Streaming OTT multi-plataforma implantado em ${c.region}. Tier ${c.category}.`
                  : `Multi-platform OTT streaming deployed across ${c.region}. ${c.category} tier.`);

              const CardWrapper = c.caseStudy ? Link : (c.url ? "a" : "article");
              const wrapperProps = c.caseStudy
                ? { to: `/clients/${c.caseStudy}`, "aria-label": `Read the ${c.brand} case study` }
                : c.url
                  ? { href: c.url, target: "_blank", rel: "noopener noreferrer", "aria-label": `Visit ${c.brand}` }
                  : {};

              const ctaLabel = c.caseStudy
                ? (lang === "fr" ? "Lire l'étude de cas" : lang === "es" ? "Leer el caso de estudio" : lang === "pt" ? "Ler o caso de estudo" : "Read case study")
                : c.url
                  ? (lang === "fr" ? "Visiter le site" : lang === "es" ? "Visitar el sitio" : lang === "pt" ? "Visitar o site" : "Visit site")
                  : null;

              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.5, delay: i * 0.04 }}
                  data-testid={`client-card-${i}`}
                >
                  <CardWrapper
                    {...wrapperProps}
                    className="card-hover glass rounded-3xl overflow-hidden flex flex-col group h-full block"
                    data-testid={c.caseStudy ? `client-card-case-${c.caseStudy}` : (c.url ? `client-card-link-${i}` : undefined)}
                  >
                    {/* Branded "screen mock" with brand wordmark */}
                    <div
                      className="relative aspect-[16/10] overflow-hidden flex items-center justify-center p-8"
                      style={{
                        background: `
                          radial-gradient(circle at 30% 20%, ${c.color}33 0%, transparent 50%),
                          radial-gradient(circle at 70% 80%, ${c.color}22 0%, transparent 50%),
                          linear-gradient(135deg, #1A2236 0%, #2A3B5C 100%)
                        `,
                      }}
                    >
                      <div className="absolute inset-0 bg-grid opacity-30" />
                      {/* Live indicator */}
                      <div className="absolute top-4 left-4 flex items-center gap-2 z-10">
                        <span className="w-1.5 h-1.5 rounded-full pulse-dot" style={{ background: c.color, boxShadow: `0 0 10px ${c.color}` }} />
                        <span className="font-mono text-[9px] uppercase tracking-widest text-slate-300">LIVE · {c.region}</span>
                      </div>
                      <div className="absolute top-4 right-4">
                        <ArrowUpRight size={18} className="text-slate-400 group-hover:text-white transition-colors" />
                      </div>
                      {/* Brand wordmark or real logo */}
                      <div className="relative text-center">
                        {c.logo ? (
                          <img
                            src={c.logo}
                            alt={c.brand}
                            className="max-h-20 md:max-h-24 max-w-[80%] mx-auto object-contain"
                            style={{ filter: `drop-shadow(0 0 32px ${c.color}66) drop-shadow(0 2px 0 rgba(0,0,0,0.3))` }}
                          />
                        ) : (
                          <div
                            className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tighter"
                            style={{
                              color: c.color,
                              textShadow: `0 0 32px ${c.color}66, 0 2px 0 rgba(0,0,0,0.3)`,
                            }}
                          >
                            {c.brand}
                          </div>
                        )}
                        <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-300">
                          {c.stat}
                        </div>
                      </div>
                      {/* Progress bar mock */}
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/5">
                        <div className="h-full" style={{ width: `${65 + i * 5}%`, background: `linear-gradient(to right, ${c.color}, transparent)` }} />
                      </div>
                    </div>

                    <div className="p-6 md:p-7">
                      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#1FE0A8]">{c.category}</div>
                      <h3 className="font-display mt-3 text-xl md:text-2xl font-bold tracking-tight text-white">{c.brand}</h3>
                      <p className="mt-3 text-sm text-slate-200 leading-relaxed">{localizedDesc}</p>
                      {ctaLabel && (
                        <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-[#1FE0A8] group-hover:text-white transition-colors">
                          {ctaLabel}
                          <ArrowUpRight size={12} />
                        </div>
                      )}
                    </div>
                  </CardWrapper>
                </motion.div>
              );
            })}
          </div>

          <p className="mt-12 text-center text-xs text-slate-400 max-w-2xl mx-auto">
            {lang === "fr" ? "Logos clients sur demande — partagez vos exigences NDA et nous fournirons une référence vérifiée."
            : lang === "es" ? "Logos de clientes bajo solicitud — comparte tus requisitos NDA y entregaremos una referencia verificable."
            : lang === "pt" ? "Logos de clientes a pedido — partilhe os seus requisitos NDA e fornecemos uma referência verificável."
            : "Live client logos available on request — share your NDA requirements and we'll provide a verified reference."}
          </p>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 border-y border-white/10 bg-[#324567]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="overline">{p.clients_testimonials_overline}</div>
          <h2 className="font-display mt-4 text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter mb-10 md:mb-12 text-white">{p.clients_testimonials_title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((tt, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.07 }}
                className="glass rounded-2xl p-7 md:p-8 flex flex-col"
                data-testid={`testimonial-${i}`}
              >
                <Quote className="text-[#1FA8FF]" size={28} />
                <p className="mt-5 text-base md:text-lg text-slate-100 leading-relaxed font-display italic">&ldquo;{tt.quote}&rdquo;</p>
                <div className="mt-6 pt-5 border-t border-white/10">
                  <div className="text-sm font-semibold text-white">{tt.author}</div>
                  <div className="text-xs text-slate-300 mt-0.5">{tt.company}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-10 text-center">
          <div className="overline justify-center inline-flex">{p.clients_partners_overline}</div>
          <h2 className="font-display mt-4 text-2xl md:text-3xl font-bold tracking-tight text-slate-200">{p.clients_partners_title}</h2>
        </div>
        <div className="marquee-fade">
          <Marquee gradient={false} speed={30} pauseOnHover>
            {PARTNERS.concat(PARTNERS).map((pr, i) => (
              <div key={i} className="mx-12 font-display text-2xl md:text-3xl text-slate-400 hover:text-white transition-colors tracking-tight">
                {pr}
              </div>
            ))}
          </Marquee>
        </div>
      </section>

      <section className="py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter text-white">
            {p.clients_cta_title_a} <span className="gradient-text">{p.clients_cta_title_b}</span>
          </h2>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/demo" data-testid="clients-cta-demo" className="btn-primary">{t("cta.demo_short")} <ArrowRight size={18} /></Link>
            <Link to="/contact" data-testid="clients-cta-contact" className="btn-ghost">{t("cta.contact_sales")}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
